if (!customElements.get('wholesale-order-builder')) {
  customElements.define('wholesale-order-builder', class WholesaleOrderBuilder extends HTMLElement {
    connectedCallback() {
      this.minimum = Number(this.dataset.minimumCents) || 100000;
      this.state = {};
      this.loading = true;
      this.updating = false;
      this.hasWholesaleLines = false;
      this.products = [...this.querySelectorAll('[data-builder-product]')];
      this.summaries = [...this.querySelectorAll('.ec-wholesale-builder__summary')];
      this.modals = [...this.querySelectorAll('[data-builder-modal]')];
      this.builderVariantIds = new Set(this.products.map((product) => String(product.dataset.variantId)));
      this.products.forEach((product) => this.bindProduct(product));
      this.modals.forEach((modal) => this.bindModal(modal));
      this.addEventListener('change', this.handleModalChange.bind(this));
      this.onKeydown = (event) => { if (event.key === 'Escape') this.modals.filter((modal) => !modal.hidden).forEach((modal) => this.closeModal(modal)); };
      document.addEventListener('keydown', this.onKeydown);
      this.querySelectorAll('[data-builder-submit]').forEach((button) => button.addEventListener('click', () => this.addToCart(button)));
      this.render();
      this.hydrateFromCart();
      if (window.subscribe && window.PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'wholesale-order-builder') this.scheduleCartHydration();
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this.cartUpdateUnsubscriber?.();
    }

    async getCart() {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Не вдалося завантажити кошик. Спробуйте ще раз.');
      return response.json();
    }

    wholesaleLines(cart) {
      return (cart?.items || []).filter((line) => this.builderVariantIds.has(String(line.variant_id)));
    }

    applyCart(cart) {
      const nextState = {};
      this.wholesaleLines(cart).forEach((line) => {
        const variantId = String(line.variant_id);
        const product = this.products.find((item) => String(item.dataset.variantId) === variantId);
        if (!product) return;
        const existing = nextState[variantId];
        nextState[variantId] = {
          id: variantId,
          quantity: (existing?.quantity || 0) + Number(line.quantity || 0),
          price: Number(product.dataset.price),
          title: product.dataset.title,
          productId: product.dataset.productId,
        };
      });
      this.state = nextState;
      this.hasWholesaleLines = Object.keys(nextState).length > 0;
    }

    analyticsPayload() {
      const items = Object.values(this.state);
      return {
        currency: 'UAH',
        value: Number((this.subtotal / 100).toFixed(2)),
        selected_product_count: items.length,
        total_quantity: items.reduce((total, item) => total + item.quantity, 0),
      };
    }

    trackAnalytics(name) {
      window.EducationCardsAnalytics?.track(name, this.analyticsPayload());
    }

    async hydrateFromCart() {
      if (this.updating) return;
      this.loading = true;
      this.render();
      try {
        this.applyCart(await this.getCart());
        this.setError();
        if (!this.analyticsViewed) {
          this.analyticsViewed = true;
          this.trackAnalytics('wholesale_builder_view');
        }
      } catch (error) {
        this.setError(error.message || 'Не вдалося завантажити кошик. Спробуйте ще раз.');
      } finally {
        this.loading = false;
        this.render();
      }
    }

    scheduleCartHydration() {
      clearTimeout(this.cartHydrationTimer);
      this.cartHydrationTimer = setTimeout(() => this.hydrateFromCart(), 80);
    }

    bindProduct(product) {
      product.querySelectorAll('[data-builder-open-modal]').forEach((trigger) => trigger.addEventListener('click', () => this.openModal(product.dataset.productId, trigger)));
      if (product.dataset.unavailable === 'true') return;
      const input = product.querySelector('[data-builder-quantity]');
      product.querySelectorAll('[data-builder-quantity-change]').forEach((button) => button.addEventListener('click', () => {
        if (!this.loading && !this.updating) this.setQuantity(product, Number(input.value || 0) + Number(button.dataset.builderQuantityChange));
      }));
      input.addEventListener('change', () => this.setQuantity(product, input.value));
      input.addEventListener('blur', () => this.setQuantity(product, input.value));
    }

    bindModal(modal) {
      modal.querySelector('[data-builder-modal-close]').addEventListener('click', () => this.closeModal(modal));
      modal.querySelector('[data-builder-modal-backdrop]').addEventListener('click', () => this.closeModal(modal));
      modal.querySelector('[data-modal-variants]')?.addEventListener('change', () => this.syncModalVariant(modal));
      modal.querySelectorAll('[data-modal-quantity-change]').forEach((button) => button.addEventListener('click', () => {
        if (this.loading || this.updating) return;
        const input = modal.querySelector('[data-modal-quantity]');
        input.value = Math.max(0, Math.floor(Number(input.value || 0) + Number(button.dataset.modalQuantityChange)));
        this.updateModalSubmitLabel(modal);
      }));
      modal.querySelector('[data-modal-quantity]')?.addEventListener('change', () => this.updateModalSubmitLabel(modal));
      modal.querySelector('[data-modal-submit]')?.addEventListener('click', () => {
        if (this.loading || this.updating) return;
        const product = this.productForId(modal.dataset.productId);
        this.setQuantity(product, modal.querySelector('[data-modal-quantity]').value, this.modalVariant(modal));
        this.closeModal(modal);
      });
      this.bindGallery(modal);
    }

    handleModalChange(event) {
      const modal = event.target.closest('[data-builder-modal]');
      if (!modal) return;
      if (event.target.matches('[data-modal-variants]')) this.syncModalVariant(modal);
      if (event.target.matches('[data-modal-quantity]')) this.updateModalSubmitLabel(modal);
    }

    productForId(productId) { return this.products.find((product) => product.dataset.productId === String(productId)); }

    modalVariant(modal) {
      const select = modal.querySelector('[data-modal-variants]');
      if (!select) {
        const product = this.productForId(modal.dataset.productId);
        return { id: product.dataset.variantId, price: Number(product.dataset.price), available: product.dataset.unavailable !== 'true' };
      }
      const option = select.options[select.selectedIndex];
      return { id: option.value, price: Number(option.dataset.price), available: !option.disabled };
    }

    openModal(productId, opener) {
      const modal = this.modals.find((item) => item.dataset.productId === String(productId));
      if (!modal) return;
      modal._opener = opener;
      const select = modal.querySelector('[data-modal-variants]');
      const currentVariant = this.productForId(productId)?.dataset.variantId;
      if (select && [...select.options].some((option) => option.value === currentVariant)) select.value = currentVariant;
      this.syncModalVariant(modal, true);
      modal.hidden = false;
      document.body.classList.add('ec-wholesale-modal-open');
      modal.querySelector('[data-builder-modal-close]').focus();
    }

    closeModal(modal) {
      modal.hidden = true;
      if (!this.modals.some((item) => !item.hidden)) document.body.classList.remove('ec-wholesale-modal-open');
      modal._opener?.focus();
    }

    syncModalVariant(modal, preserveCardVariant = false) {
      const product = this.productForId(modal.dataset.productId);
      const variant = this.modalVariant(modal);
      const input = modal.querySelector('[data-modal-quantity]');
      const price = modal.querySelector('[data-modal-price]');
      if (price) price.innerHTML = `${this.formatMoney(variant.price)} <span>/ шт</span>`;
      if (input) {
        input.value = this.state[variant.id]?.quantity || 0;
        input.disabled = this.loading || this.updating || !variant.available;
      }
      modal.querySelectorAll('[data-modal-quantity-change]').forEach((button) => { button.disabled = this.loading || this.updating || !variant.available; });
      if (!preserveCardVariant && product && variant.available) this.setCardVariant(product, variant);
      this.updateModalSubmitLabel(modal);
    }

    setCardVariant(product, variant) {
      product.dataset.variantId = variant.id;
      product.dataset.price = variant.price;
      product.dataset.unavailable = variant.available ? '' : 'true';
      const price = product.querySelector('.ec-wholesale-builder__price');
      if (price) price.innerHTML = `${this.formatMoney(variant.price)} <span>/ шт</span>`;
      const input = product.querySelector('[data-builder-quantity]');
      if (input) input.value = this.state[variant.id]?.quantity || 0;
      product.querySelectorAll('[data-builder-quantity], [data-builder-quantity-change]').forEach((control) => { control.disabled = this.loading || this.updating || !variant.available; });
    }

    updateModalSubmitLabel(modal) {
      const button = modal.querySelector('[data-modal-submit]');
      const input = modal.querySelector('[data-modal-quantity]');
      if (!button || !input) return;
      if (input.disabled) { button.disabled = true; button.textContent = 'Наразі недоступно'; return; }
      button.disabled = false;
      button.textContent = Number(input.value || 0) > 0 ? 'Оновити кількість у наборі' : 'Додати до набору';
    }

    bindGallery(modal) {
      const slides = modal.querySelector('[data-modal-slides]');
      const allSlides = [...modal.querySelectorAll('[data-modal-slide]')];
      const dots = [...modal.querySelectorAll('[data-modal-dot]')];
      if (!slides || allSlides.length < 2) return;
      const show = (index) => {
        const next = ((index % allSlides.length) + allSlides.length) % allSlides.length;
        slides.scrollTo({ left: allSlides[next].offsetLeft, behavior: 'smooth' });
        modal._galleryIndex = next;
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === next));
      };
      modal._showGallery = show;
      modal.querySelector('[data-modal-previous]')?.addEventListener('click', () => show((modal._galleryIndex || 0) - 1));
      modal.querySelector('[data-modal-next]')?.addEventListener('click', () => show((modal._galleryIndex || 0) + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => show(index)));
      slides.addEventListener('scroll', () => {
        const index = allSlides.reduce((nearest, slide, current) => Math.abs(slide.offsetLeft - slides.scrollLeft) < Math.abs(allSlides[nearest].offsetLeft - slides.scrollLeft) ? current : nearest, 0);
        modal._galleryIndex = index;
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      }, { passive: true });
      show(0);
    }

    setQuantity(product, value, overrideVariant) {
      if (this.loading || this.updating) return;
      const variant = overrideVariant || { id: product.dataset.variantId, price: Number(product.dataset.price), available: product.dataset.unavailable !== 'true' };
      if (!variant.available) return;
      const previousQuantity = Number(this.state[variant.id]?.quantity || 0);
      const previousSubtotal = this.subtotal;
      const quantity = Math.max(0, Math.floor(Number(value) || 0));
      if (quantity) this.state[variant.id] = { id: variant.id, quantity, price: Number(variant.price), title: product.dataset.title, productId: product.dataset.productId };
      else delete this.state[variant.id];
      this.setCardVariant(product, variant);
      if (previousQuantity === 0 && quantity > 0) this.trackAnalytics('wholesale_item_add');
      if (previousQuantity > 0 && quantity === 0) this.trackAnalytics('wholesale_item_remove');
      if (previousSubtotal < this.minimum && this.subtotal >= this.minimum) this.trackAnalytics('wholesale_minimum_reached');
      this.render();
    }

    get subtotal() { return Object.values(this.state).reduce((total, item) => total + item.price * item.quantity, 0); }
    formatMoney(cents) { return `${new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 }).format(cents / 100)} грн`; }

    render() {
      const total = this.subtotal;
      const remaining = Math.max(0, this.minimum - total);
      const complete = total >= this.minimum && total > 0;
      const items = Object.values(this.state);
      this.products.forEach((product) => {
        const selected = items.some((item) => item.productId === product.dataset.productId || item.id === product.dataset.variantId);
        product.classList.toggle('is-selected', selected);
        const input = product.querySelector('[data-builder-quantity]');
        const disabled = this.loading || this.updating || product.dataset.unavailable === 'true';
        if (input) {
          input.value = this.state[product.dataset.variantId]?.quantity || 0;
          input.disabled = disabled;
        }
        product.querySelectorAll('[data-builder-quantity-change]').forEach((control) => { control.disabled = disabled; });
      });
      this.summaries.forEach((summary) => {
        summary.classList.toggle('is-complete', complete);
        summary.querySelector('[data-builder-subtotal]').textContent = this.formatMoney(total);
        summary.querySelector('[data-builder-progress]').style.width = `${Math.min(100, (total / this.minimum) * 100)}%`;
        summary.querySelector('[data-builder-minimum-status]').textContent = complete ? `${this.formatMoney(total)} — мінімум виконано` : `${this.formatMoney(total)} / ${this.formatMoney(this.minimum)}`;
        summary.querySelector('[data-builder-remaining]').textContent = complete ? 'Можна додати набір до кошика' : `До мінімуму залишилося ${this.formatMoney(remaining)}`;
        const list = summary.querySelector('[data-builder-selected-list]');
        if (list) {
          list.innerHTML = '';
          items.slice(0, 3).forEach((item) => { const entry = document.createElement('li'); const title = document.createElement('span'); const quantity = document.createElement('strong'); title.textContent = item.title; quantity.textContent = `×${item.quantity}`; entry.append(title, quantity); list.appendChild(entry); });
          if (items.length > 3) { const entry = document.createElement('li'); entry.textContent = `Ще ${items.length - 3} моделей`; list.appendChild(entry); }
          list.hidden = items.length === 0;
        }
        const button = summary.querySelector('[data-builder-submit]');
        const blocked = this.loading || this.updating || !complete;
        button.disabled = blocked;
        button.setAttribute('aria-disabled', String(blocked));
        button.textContent = this.loading ? 'Завантаження кошика…' : this.updating ? 'Оновлення кошика…' : this.hasWholesaleLines ? 'Оновити кошик' : 'Додати набір до кошика';
      });
    }

    setError(message = '') { this.summaries.forEach((summary) => { const target = summary.querySelector('[data-builder-error]'); target.textContent = message; target.hidden = !message; }); }

    async addToCart(button) {
      if (this.loading || this.updating || this.subtotal < this.minimum) return;
      this.setError(); this.updating = true; button.setAttribute('aria-busy', 'true'); this.render();
      try {
        const currentCart = await this.getCart();
        const existingWholesaleLines = this.wholesaleLines(currentCart);
        const hadWholesaleLines = existingWholesaleLines.length > 0;
        const seenVariants = new Set();
        const updates = {};
        existingWholesaleLines.forEach((line) => {
          const variantId = String(line.variant_id);
          const targetQuantity = Number(this.state[variantId]?.quantity || 0);
          updates[line.key] = seenVariants.has(variantId) ? 0 : targetQuantity;
          seenVariants.add(variantId);
        });
        const addItems = Object.values(this.state)
          .filter((item) => item.quantity > 0 && !seenVariants.has(String(item.id)))
          .map(({ id, quantity }) => ({ id: Number(id), quantity }));
        const drawer = document.querySelector('cart-drawer');
        const sectionData = drawer?.getSectionsToRender ? { sections: drawer.getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname } : {};
        if (drawer) drawer.setActiveElement(button);
        let mutationResult;
        if (Object.keys(updates).length) mutationResult = await this.mutateCart(`${this.dataset.cartUrl}/update.js`, { updates, ...sectionData });
        if (addItems.length) mutationResult = await this.mutateCart(`${this.dataset.cartAddUrl}.js`, { items: addItems, ...sectionData });
        const updatedCart = await this.getCart();
        this.applyCart(updatedCart);
        this.trackAnalytics(hadWholesaleLines ? 'wholesale_cart_update' : 'wholesale_cart_add');
        if (drawer && mutationResult?.sections) drawer.renderContents(mutationResult);
        if (window.publish && window.PUB_SUB_EVENTS?.cartUpdate) await window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'wholesale-order-builder', cartData: updatedCart });
      } catch (error) { this.setError(error.message || 'Сталася помилка. Спробуйте ще раз.'); }
      finally { this.updating = false; button.removeAttribute('aria-busy'); this.render(); }
    }

    async mutateCart(url, payload) {
      const response = await fetch(url, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Не вдалося оновити кошик.');
      return result;
    }
  });
}
